import type { Organization } from '@types';
import { COUNTRY_CODES } from '../common/data';
import { regionForCountry } from '../../functions/src/common/organizationRegion';

export interface RegionalFeed {
    label: string;
    region: string;
    urls: string[];
    countries?: string[];
    regions?: string[];
    global?: boolean;
}

const countriesByCode = new Map<string, string>();
for (const [name, codes] of Object.entries(COUNTRY_CODES)) {
    countriesByCode.set(codes.iso2.toUpperCase(), name);
    countriesByCode.set(codes.iso3.toUpperCase(), name);
}

function countryName(value: unknown): string {
    const raw = String(value || '').trim();
    return countriesByCode.get(raw.toUpperCase()) || raw;
}

function countryKey(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function organizationCoverageCountries(organization: Pick<Organization, 'headquartersAddress' | 'additionalLocations' | 'newsFeedCountries'>): string[] {
    const locations = [organization.headquartersAddress, ...(organization.additionalLocations || [])];
    const names: string[] = [];
    const seen = new Set<string>();
    for (const country of organization.newsFeedCountries || []) {
        const name = countryName(country);
        if (name && !seen.has(countryKey(name))) {
            seen.add(countryKey(name));
            names.push(name);
        }
    }
    for (const location of locations) {
        const name = countryName(location?.country || location?.countryCode);
        if (!name || seen.has(countryKey(name)))
            continue;
        seen.add(countryKey(name));
        names.push(name);
    }
    return names;
}

export function regionalNewsFeeds<T extends RegionalFeed>(feeds: T[], countries: string[], maxFeeds?: number): T[] {
    const regionalFeeds = feeds.filter(feed => !feed.global);
    if (!countries.length)
        return maxFeeds ? regionalFeeds.slice(0, maxFeeds) : regionalFeeds;

    const selected: T[] = [];
    const seen = new Set<T>();
    const append = (feed: T) => {
        if (seen.has(feed) || (maxFeeds && selected.length >= maxFeeds))
            return;
        seen.add(feed);
        selected.push(feed);
    };

    // Give each location its own channel before filling remaining slots.
    for (const country of countries) {
        const match = regionalFeeds.find(feed => !seen.has(feed) && feed.countries?.some(name => countryKey(name) === countryKey(country)));
        if (match)
            append(match);
    }
    for (const feed of regionalFeeds)
        if (feed.countries?.some(name => countries.some(country => countryKey(name) === countryKey(country))))
            append(feed);

    for (const country of countries) {
        const region = regionForCountry(country);
        const match = regionalFeeds.find(feed => !seen.has(feed) && feed.regions?.includes(region));
        if (match)
            append(match);
    }
    for (const feed of regionalFeeds)
        if (feed.regions?.some(region => countries.some(country => regionForCountry(country) === region)))
            append(feed);

    return selected;
}

export function countryNewsFeeds<T extends RegionalFeed>(feeds: T[], country: string, maxFeeds?: number): T[] {
    const target = countryKey(countryName(country));
    const matched = feeds.filter(feed => !feed.global && feed.countries?.some(name => countryKey(countryName(name)) === target));
    const selected = [...matched, ...feeds.filter(feed => feed.global)];
    return maxFeeds ? selected.slice(0, maxFeeds) : selected;
}
